import _ from "lodash";
import {useTranslation} from "react-i18next";
import {Link, Outlet, useLocation, useNavigate, useParams} from "react-router-dom";
import {useSetAtom} from "jotai";
import {Divider, Stack, Tab, Tabs, Typography} from "@mui/material";
import {AppLayout} from "../AppLayout";
import {MenuItem} from "../../types/menuItem";
import {useRouterParamsStoreSync} from "../../store/routerStore";
import {CompanyHierarchyView, RenderCompanyItemFn} from "../companies/CompanyHierarchyView";
import {HighlightedText} from "../base/HighlightedText";
import {useAccessMarker} from "../../hooks/accessMarkerHooks";
import {ViewAuthorizationGuard} from "../ViewAuthorizationGuard";
import {getResourcePageErrorMessageTSQ} from "../../utils/errorUtils";
import {ErrorMessageView} from "../base/ErrorMessageView";
import {ResultsDatePicker} from "../base/ResultsDatePicker";
import {activeDateLoadable} from "../../store/activeDateStore";
import {useLoadableValueOrDefault} from "../../hooks/jotaiHooks";
import {ALL_ACCESS_FUNCTIONS} from "../../utils/accessControlUtils";
import {useCompanyIdPersist} from "../../hooks/companyIdPersistHooks";
import {CompanyIcon} from "../icons/CompanyIcon";
import {showActiveFieldsAtom} from "../../store/filterSelectionStore";
import {useCompany, useCompanyHierarchy} from "../../hooks/companiesHooks";

export const OrganizationPage: React.FC = () => {
    useRouterParamsStoreSync();
    useCompanyIdPersist();

    const activeCompanyState = useCompany();
    const setActiveFieldsFilter = useSetAtom(showActiveFieldsAtom);
    setActiveFieldsFilter(false);
    const pageError = getResourcePageErrorMessageTSQ(activeCompanyState);

    const access = useAccessMarker(ACCESS_FUNCTIONS);

    return (
        <AppLayout sidePanel={access.canViewOrganizationsPage && <OrganizationPagePanel />}>
            {pageError && <ErrorMessageView message={pageError} />}
            {!pageError && access.hasData && (
                <ViewAuthorizationGuard isAuthorized={access.canViewOrganizationsPage}>
                    <Outlet />
                </ViewAuthorizationGuard>
            )}
        </AppLayout>
    );
};

const OrganizationPagePanel: React.FC = () => {
    const {t} = useTranslation();
    const {companyId} = useParams();
    const pathname = useLocation().pathname;
    const company = useCompany();
    const companyHierarchyState = useCompanyHierarchy();
    const companyHierarchy = companyHierarchyState.isSuccess ? companyHierarchyState.data : [];
    const date = useLoadableValueOrDefault(activeDateLoadable, null);
    const access = useAccessMarker(ACCESS_FUNCTIONS);
    const navigate = useNavigate();

    const onCompanySelect = (_companyId: string) => {
        navigate({
            pathname: `/organization/${_companyId}`,
            search: `parentCompany=${_companyId}`,
        });
    };

    return (
        <Stack
            direction="column"
            sx={{height: "calc(100% - 2.25rem)", padding: "1.125rem"}}
            overflow="hidden"
            gap="1rem"
        >
            <ResultsDatePicker date={date} sx={{width: "100%"}} disableElevation disabled />
            <Stack direction="column">
                <Typography textTransform="uppercase" sx={{color: "#737990"}}>
                    {t("organization_page.title")}
                </Typography>
                <Divider sx={{marginTop: "0.3rem", marginBottom: "0.7rem"}} />
                {company?.data && (
                    <>
                        <Stack direction="row" gap={1} minWidth="0" alignItems="center" marginBottom="0.75rem">
                            <CompanyIcon
                                sx={{
                                    stroke: "#6476CB",
                                    strokeWidth: "2",
                                    width: "1rem",
                                    height: "1rem",
                                }}
                            />
                            <Typography textOverflow="ellipsis" noWrap>
                                {company?.data?.properties.name}
                            </Typography>
                        </Stack>
                        <Tabs
                            value={pathname}
                            orientation="vertical"
                            textColor="inherit"
                            variant="fullWidth"
                            TabIndicatorProps={{
                                sx: {
                                    width: 0,
                                },
                            }}
                            sx={{
                                minHeight: "auto",
                                color: "#000000A0",
                                "& a:hover": {backgroundColor: "#eceff5"},
                                "& .Mui-selected": {backgroundColor: "#f3f5fa"},
                            }}
                        >
                            {menuItems({
                                companyId,
                                showUsersMenu: access.canViewCompanyMembers,
                                showFarmsMenu: access.canViewCompanyFarms,
                                showOrdersMenu: access.canViewCompanyOrders,
                            }).map((item) => (
                                <Tab
                                    color="primary"
                                    key={item.href}
                                    label={t(item.i18nLabel)}
                                    icon={item.icon}
                                    iconPosition="start"
                                    value={item.href}
                                    id={item.href}
                                    component={Link}
                                    to={item.href}
                                    sx={{
                                        minHeight: "2.25rem",
                                        height: "2.5rem",
                                        letterSpacing: ".04em",
                                        justifyContent: "left",
                                        whiteSpace: "nowrap",
                                        fontSize: "0.875rem",
                                        opacity: 1,
                                        px: "2rem",
                                        py: "0.5rem",
                                    }}
                                />
                            ))}
                        </Tabs>
                    </>
                )}
            </Stack>
            {companyHierarchy.length > 1 && (
                <Stack direction="column" overflow="hidden">
                    <Typography textTransform="uppercase" sx={{color: "#737990"}}>
                        {t("organization_page.enterprise")}
                    </Typography>
                    <Divider sx={{marginBottom: "0.75rem", marginTop: "0.3rem"}} />
                    {companyId?.length && (
                        <CompanyHierarchyView
                            companies={companyHierarchy}
                            isLoading={companyHierarchyState.isLoading}
                            selectedCompanyId={companyId}
                            renderItem={renderCompanyItem}
                            onCompanySelect={onCompanySelect}
                        />
                    )}
                </Stack>
            )}
        </Stack>
    );
};

const menuItems = (props: {
    companyId: string | undefined;
    showUsersMenu: boolean;
    showFarmsMenu: boolean;
    showOrdersMenu: boolean;
}): MenuItem[] => [
    {
        i18nLabel: "organization_page.menu.company_details",
        href: props.companyId ? `/organization/${props.companyId}` : "/organization",
    },
    ...(props.showUsersMenu
        ? [
              {
                  i18nLabel: "organization_page.menu.users",
                  href: props.companyId ? `/organization/${props.companyId}/users` : "/organization/users",
              },
          ]
        : []),
    ...(props.showOrdersMenu
        ? [
              {
                  i18nLabel: "organization_page.menu.orders",
                  href: props.companyId ? `/organization/${props.companyId}/orders` : "/organization/orders",
              },
          ]
        : []),
    ...(props.showFarmsMenu
        ? [
              {
                  i18nLabel: "organization_page.menu.farms",
                  href: props.companyId ? `/organization/${props.companyId}/farms` : "/organization/farms",
              },
          ]
        : []),
];

const renderCompanyItem: RenderCompanyItemFn = ({company, searchFilter}) => (
    <Typography fontSize={14} textOverflow="ellipsis" noWrap>
        <HighlightedText text={company.properties.name} highlight={searchFilter} />
    </Typography>
);

const ACCESS_FUNCTIONS = _.pick(
    ALL_ACCESS_FUNCTIONS,
    "canViewOrganizationsPage",
    "canViewCompanyFarms",
    "canViewCompanyMembers",
    "canViewCompanyOrders",
);
